import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { Aviso, BotonSuave, Cargando, Hora, INPUT_CLASS } from "./ui";
import type { PasoProps } from "./types";

/**
 * PASO 1 (restaurantes): días abiertos y TURNOS (Comida, Cena…). Todo
 * restaurante nace con Comida 13:00-16:00 y Cena 20:00-23:30 los siete días
 * (`auth/controller.ts`), así que esto no es crear nada: es repasar que
 * coincide con la realidad del local, que es de donde salen las horas que se
 * ofrecen en el widget.
 *
 * Se guarda con `PUT /schedule/config`, que trabaja sobre la sede activa (la
 * cabecera `X-Location-Id` la manda el cliente de la API). El resto de la
 * configuración —duración, antelación, aforo— se queda en la pantalla de
 * Horarios.
 */

const DIAS = [
  { key: "1", largo: "Lunes" },
  { key: "2", largo: "Martes" },
  { key: "3", largo: "Miércoles" },
  { key: "4", largo: "Jueves" },
  { key: "5", largo: "Viernes" },
  { key: "6", largo: "Sábado" },
  { key: "0", largo: "Domingo" },
] as const;

interface Turno {
  name: string;
  startTime: string;
  endTime: string;
  enabled: boolean;
}

const MAX_TURNOS = 4;

function minutos(hora: string): number {
  const [h, m] = hora.split(":").map(Number);
  return h * 60 + m;
}

export default function PasoHorarioMesas({
  refrescar,
  registrarAvance,
  registrarTextoAvance,
}: PasoProps) {
  const { showToast } = useGlobalToast();
  const [dias, setDias] = useState<Record<string, boolean> | null>(null);
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await apiClient.get("/schedule/config");
        const config = res.data?.config ?? res.data?.data ?? {};
        if (!vivo) return;
        const semana: Record<string, boolean> = {};
        for (const d of DIAS) {
          semana[d.key] = config?.weeklySchedule?.[d.key] !== false;
        }
        setDias(semana);
        setTurnos(
          Array.isArray(config?.shifts) && config.shifts.length > 0
            ? config.shifts
            : [
                { name: "Comida", startTime: "13:00", endTime: "16:00", enabled: true },
                { name: "Cena", startTime: "20:00", endTime: "23:30", enabled: true },
              ],
        );
      } catch {
        if (!vivo) return;
        const semana: Record<string, boolean> = {};
        for (const d of DIAS) semana[d.key] = true;
        setDias(semana);
        setTurnos([
          { name: "Comida", startTime: "13:00", endTime: "16:00", enabled: true },
          { name: "Cena", startTime: "20:00", endTime: "23:30", enabled: true },
        ]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const cambiarTurno = (indice: number, patch: Partial<Turno>) => {
    setTurnos((prev) => prev.map((t, i) => (i === indice ? { ...t, ...patch } : t)));
  };

  const guardar = useCallback(async (): Promise<boolean> => {
    if (!dias) return false;

    if (!Object.values(dias).some(Boolean)) {
      showToast("Marca al menos un día abierto", "error");
      return false;
    }
    const activos = turnos.filter((t) => t.enabled);
    if (activos.length === 0) {
      showToast("Deja al menos un turno activo (Comida o Cena)", "error");
      return false;
    }
    for (const turno of activos) {
      if (!turno.name.trim()) {
        showToast("Cada turno necesita un nombre (Comida, Cena…)", "error");
        return false;
      }
      if (minutos(turno.endTime) <= minutos(turno.startTime)) {
        showToast(
          `${turno.name}: la hora de fin debe ser posterior a la de inicio`,
          "error",
        );
        return false;
      }
    }
    const ordenados = [...activos].sort(
      (a, b) => minutos(a.startTime) - minutos(b.startTime),
    );
    for (let i = 1; i < ordenados.length; i++) {
      if (minutos(ordenados[i].startTime) < minutos(ordenados[i - 1].endTime)) {
        showToast("Los turnos no pueden solaparse", "error");
        return false;
      }
    }

    try {
      setGuardando(true);
      await apiClient.put("/schedule/config", {
        weeklySchedule: dias,
        shifts: turnos,
      });
      refrescar();
      showToast("Horario guardado");
      return true;
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo guardar el horario"),
        "error",
      );
      return false;
    } finally {
      setGuardando(false);
    }
  }, [dias, turnos, refrescar, showToast]);

  useEffect(() => {
    registrarAvance(guardar);
    registrarTextoAvance(guardando ? "Guardando…" : "Guardar y continuar");
    return () => {
      registrarAvance(null);
      registrarTextoAvance(null);
    };
  }, [guardar, guardando, registrarAvance, registrarTextoAvance]);

  if (!dias) return <Cargando texto="Cargando tu horario…" />;

  return (
    <div className="space-y-4">
      <Aviso>
        Tus turnos son las horas a las que se puede reservar mesa. Vienen puestos
        de fábrica (Comida y Cena): <strong>cámbialos a los tuyos</strong> y
        cierra los días que no abres.
      </Aviso>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1.5">¿Qué días abres?</p>
        <div className="flex flex-wrap gap-1.5">
          {DIAS.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDias((prev) => (prev ? { ...prev, [d.key]: !prev[d.key] } : prev))}
              className={`px-3 h-9 rounded-lg text-sm font-semibold transition-colors ${
                dias[d.key]
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              {d.largo.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Turnos</p>
        {turnos.map((turno, i) => (
          <div
            key={i}
            className="flex items-center gap-2 flex-wrap rounded-lg border border-gray-200 p-2"
          >
            <input
              className={`${INPUT_CLASS} w-28`}
              value={turno.name}
              onChange={(e) => cambiarTurno(i, { name: e.target.value })}
              aria-label={`Nombre del turno ${i + 1}`}
              placeholder="Comida"
            />
            <Hora
              ariaLabel={`${turno.name}: hora de inicio`}
              value={turno.startTime}
              onChange={(v) => cambiarTurno(i, { startTime: v })}
            />
            <span className="text-gray-400 text-sm">a</span>
            <Hora
              ariaLabel={`${turno.name}: hora de fin`}
              value={turno.endTime}
              onChange={(v) => cambiarTurno(i, { endTime: v })}
            />
            <label className="flex items-center gap-1.5 text-xs text-gray-600 ml-auto">
              <input
                type="checkbox"
                checked={turno.enabled}
                onChange={(e) => cambiarTurno(i, { enabled: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Activo
            </label>
            {turnos.length > 1 && (
              <BotonSuave
                tono="peligro"
                onClick={() => setTurnos((prev) => prev.filter((_, j) => j !== i))}
              >
                Quitar
              </BotonSuave>
            )}
          </div>
        ))}
        {turnos.length < MAX_TURNOS && (
          <BotonSuave
            onClick={() =>
              setTurnos((prev) => [
                ...prev,
                { name: "Desayuno", startTime: "08:00", endTime: "11:00", enabled: true },
              ])
            }
          >
            <span className="inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Añadir otro turno
            </span>
          </BotonSuave>
        )}
      </div>
    </div>
  );
}
